import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';
import { IterableProvider } from '../../providers/marketing/iterable';
import { LoggerEvent, LoggerProvider } from '../../providers/logger/logger';
import { OrderProvider } from '../../providers/order/order';
import { StorageProvider } from '../../providers/storage/storage';
import { StorageType } from '../../model/types/storage-type';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { BranchProvider } from '../../providers/analytics/branch';
import { SegmentProvider } from '../../providers/analytics/segment';
import { AnalyticsEventType } from '../../model/types/analytics.const';
import { RouteProvider } from '../../providers/route/route';
import { DeviceProvider } from '../../providers/device';
import { APP_CONFIG } from '../../environment';
import { OpenNativeSettings } from '@ionic-native/open-native-settings';
import { GeolocationPermissionStatus } from '../../model/types/geolocation-compliance-type';
declare var Accertify: any;
@Injectable()
export class PermissionProvider {

  // Prompts still unanswered - lets the bridge tell a real denial from a timeout.
  private static pendingPermissionRequests = new Set<string>();

  static isPermissionRequestPending(key: string): boolean {
    return PermissionProvider.pendingPermissionRequests.has(key);
  }

  private static markPermissionRequestPending(key: string) {
    PermissionProvider.pendingPermissionRequests.add(key);
  }

  private static resolvePermissionRequest(key: string) {
    PermissionProvider.pendingPermissionRequests.delete(key);
  }

  constructor(
    private iterable: IterableProvider,
    private platform: Platform,
    private orderProvider: OrderProvider,
    private storageProvider: StorageProvider,
    private utilsProvider: UtilsProvider,
    private segmentProvider: SegmentProvider,
    private branch: BranchProvider,
    private routeProvider: RouteProvider,
    private deviceProvider: DeviceProvider,
    private openNativeSettings: OpenNativeSettings
  ) { }

  async getLocationAuthorizationStatus(): Promise<GeolocationPermissionStatus> {
    return new Promise<GeolocationPermissionStatus>((resolve) => {
      if (!this.platform.is('cordova')) {
        this.storageProvider.get(StorageType.LocationAccess).then((value) => {
          resolve(value === 'true' ? GeolocationPermissionStatus.Granted : GeolocationPermissionStatus.NotRequested);
        }, () => resolve(GeolocationPermissionStatus.NotRequested));
        return;
      }
      const plugin = this.deviceProvider.getRmtLocationPlugin();
      const timeout = this.createTimeout((v) => resolve(v === false ? GeolocationPermissionStatus.Unknown : v), 'location authorization status');
      if (!plugin) {
        this.checkLocationPermission().then((granted) => {
          window.clearTimeout(timeout);
          resolve(granted ? GeolocationPermissionStatus.Granted : GeolocationPermissionStatus.Unknown);
        });
        return;
      }
      try {
        plugin.getAuthorizationStatus((status) => {
          window.clearTimeout(timeout);
          resolve(PermissionProvider.toPermissionStatus(status));
        }, (err) => {
          window.clearTimeout(timeout);
          LoggerProvider.error(this, LoggerEvent.GeneralException, 'rmt-location getAuthorizationStatus failed', err);
          resolve(GeolocationPermissionStatus.Unknown);
        });
      } catch (ex) {
        window.clearTimeout(timeout);
        LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'rmt-location getAuthorizationStatus threw');
        resolve(GeolocationPermissionStatus.Unknown);
      }
    });
  }

  async requestLocationAuthorizationDetailed(timeoutMs = 60000): Promise<GeolocationPermissionStatus> {
    return new Promise<GeolocationPermissionStatus>((resolve) => {
      if (!this.platform.is('cordova')) {
        this.storageProvider.set(StorageType.LocationAccess, 'true');
        resolve(GeolocationPermissionStatus.Granted);
        return;
      }
      if (APP_CONFIG.automationTest) {
        resolve(GeolocationPermissionStatus.DeniedAlways);
        return;
      }
      const plugin = this.deviceProvider.getRmtLocationPlugin();
      if (!plugin) {
        this.requestLocationPermission(timeoutMs).then((granted) => {
          resolve(granted ? GeolocationPermissionStatus.Granted : GeolocationPermissionStatus.DeniedAlways);
        });
        return;
      }
      const timeout = this.createTimeout((v) => resolve(v === false ? GeolocationPermissionStatus.Unknown : v), 'request location authorization', timeoutMs);
      PermissionProvider.markPermissionRequestPending(StorageType.LocationAccess);
      const finish = (status: GeolocationPermissionStatus) => {
        window.clearTimeout(timeout);
        PermissionProvider.resolvePermissionRequest(StorageType.LocationAccess);
        this.storageProvider.set(StorageType.LocationAccess, 'true');
        LoggerProvider.info(this, 'Location authorization requested (rmt-location)', status);
        resolve(status);
      };
      try {
        plugin.requestAuthorization((status) => finish(PermissionProvider.toPermissionStatus(status)), (err) => {
          LoggerProvider.error(this, LoggerEvent.GeneralException, 'rmt-location requestAuthorization failed', err);
          finish(GeolocationPermissionStatus.Unknown);
        });
      } catch (ex) {
        LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'rmt-location requestAuthorization threw');
        finish(GeolocationPermissionStatus.Unknown);
      }
    });
  }

  async openLocationSettings(status: GeolocationPermissionStatus = GeolocationPermissionStatus.DeniedAlways): Promise<boolean> {
    if (!this.platform.is('cordova')) {
      return false;
    }
    let target = 'application_details';
    if (status === GeolocationPermissionStatus.ServicesDisabled && this.platform.is('android')) {
      target = 'location';
    }
    try {
      await this.openNativeSettings.open(target);
      return true;
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Failed to open native settings for location');
      return false;
    }
  }

  static toPermissionStatus(raw: any): GeolocationPermissionStatus {
    switch (raw) {
      case GeolocationPermissionStatus.Granted:
      case GeolocationPermissionStatus.NotRequested:
      case GeolocationPermissionStatus.DeniedOnce:
      case GeolocationPermissionStatus.DeniedAlways:
      case GeolocationPermissionStatus.ServicesDisabled:
        return raw;
      default:
        return GeolocationPermissionStatus.Unknown;
    }
  }
  /**
   * Check if notification permission is granted
   */
  checkNotificationPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.platform.is('cordova')) {
            this.iterable.isNotificationPermissionGranted((success: any) => {
              LoggerProvider.info(this, success ? "Notification push token is available" : "Notification push token is not available");
              resolve(success ? true : false);
              // In case user rejects the permission in native popup that handling is pending.
            })
      } else {
        // For non cordova, we have to show the UI for tracking details
        this.storageProvider.get(StorageType.NotificationAccess).then((value) => {
          resolve(value && value === "true");
        });
      }
    });
  }

  /**
   * Check if notification permission screen Accessed
   */

  checkNotificationPermissionScreenAccessed(): Promise<boolean> {
    return new Promise((resolve) => {
      this.storageProvider.get(StorageType.NotificationAccess).then((value) => {
        if (value === "true") {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  /**
   * Request notification permission
   *
   * NotificationAccess means "answered", not "granted" - it gates the once-per-install
   * interstitial. Written natively even under RMT5 onboarding so a relogin won't re-show it.
   */
  requestNotificationPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      if (this.platform.is('cordova')) {
        PermissionProvider.markPermissionRequestPending(StorageType.NotificationAccess);
        this.iterable.registerForPush((success: any) => {
          LoggerProvider.info(this, 'Notification permission requested', success);
          const granted = success === 1 || success === true;
          PermissionProvider.resolvePermissionRequest(StorageType.NotificationAccess);
          this.storageProvider.set(StorageType.NotificationAccess, 'true');
          LoggerProvider.info(this, granted ? "Iterable - Registered for Push Notification" : "Iterable - Failed to register for Push Notification", granted);
          resolve(granted);
        }, () => {
          PermissionProvider.resolvePermissionRequest(StorageType.NotificationAccess);
          this.storageProvider.set(StorageType.NotificationAccess, 'true');
          LoggerProvider.info(this, "Iterable - Failed to register for Push Notification");
          resolve(false);
        });
      } else {
        resolve(true);
      }
    });
  }

  /**
   * Check if Location permission is granted
   */
  checkLocationPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      // Need to remove timeout from beta as well
      const timeout = this.createTimeout(resolve, 'check location permissions');
      if (this.platform.is('cordova')) {
            Accertify.isLocationPermissionGranted((success) => {
              if (timeout)
                window.clearTimeout(timeout);
              LoggerProvider.info(this, success ? "Location permission is already granted" : "Location permission is not granted", success);
              resolve(success ? true : false);
            });

      } else {
        // For non cordova, we have to show the UI for tracking details
        this.storageProvider.get(StorageType.LocationAccess).then((value) => {
          if (timeout)
            window.clearTimeout(timeout);
          resolve(value && value === "true");
        });
      }
    });
  }

  /**
   * Check if Location permission screen Accessed
   */
  checkLocationPermissionScreenAccessed(): Promise<boolean> {
    return new Promise((resolve) => {
      const timeout = this.createTimeout(resolve, 'location permissions accessed');
      this.storageProvider.get(StorageType.LocationAccess).then((value) => {
        if (timeout)
          window.clearTimeout(timeout);

        if (value === "true") {
          resolve(true);
        } else {
          resolve(false);
        }
      });
    });
  }

  /**
   * Request Location permission
   *
   * Persisted only from the real OS callback, so a timed-out caller can't mirror a
   * phantom answer back over the bridge.
   * @param timeoutMs emergency timeout; bridge onboarding passes 60s.
   */
  requestLocationPermission(timeoutMs = 5000): Promise<boolean> {
    return new Promise((resolve) => {
      const timeout = this.createTimeout(resolve, 'request location permissions', timeoutMs);
      if (this.platform.is('cordova')) {
        if (APP_CONFIG.automationTest) {
            resolve(false);
            return;
        }
        LoggerProvider.info(this, 'Location permission requested');
        PermissionProvider.markPermissionRequestPending(StorageType.LocationAccess);
        Accertify.requestLocationPermission((success) => {
          if (timeout)
            window.clearTimeout(timeout);

          LoggerProvider.info(this, 'Location permission requested', success);
          PermissionProvider.resolvePermissionRequest(StorageType.LocationAccess);
          this.storageProvider.set(StorageType.LocationAccess, 'true');
          LoggerProvider.info(this, (success) ? 'Location permission request granted' : 'Location permission request failed', success);
          resolve(!!success);
        });
      } else {
        if (timeout)
          window.clearTimeout(timeout);

        resolve(true);
      }
    });
  }

  /**
   * Check if ATT (App Tracking Transparency) permission is granted
   */
  checkATTPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      const timeout = this.createTimeout(resolve, 'check ATT permissions');
      return this.storageProvider.get(StorageType.ConsentIDFA).then((value) => {
        if (timeout)
          window.clearTimeout(timeout);

        if ((value == null || value == 'true') && !StorageProvider.secureStorageSkip) {
          resolve(false);
        } else {
          resolve(true);
        }
      });
    });
  }

  /**
   * Request ATT (App Tracking Transparency) permission
   */
  requestATTPermission(): Promise<boolean> {
    return new Promise((resolve) => {
      const timeout = this.createTimeout(resolve, 'request ATT permissions');
      if (this.platform.is('cordova')) {
        this.storageProvider.set(StorageType.ConsentIDFA, 'false'); // setting it to false if user gone through this once
        if (this.utilsProvider.isConsentModeEnabled() === false && this.platform.is('android')) {
          if (timeout)
            window.clearTimeout(timeout);

          LoggerProvider.info(this, 'Accepting consent mode in background for Android');
          this.branch.setDMAParamsForEEA(); // Consent from Branch 
          this.segmentProvider.consentIDFA(); // Consent from Firebase 
          this.storageProvider.set(StorageType.ConsentIDFAPermission, 'true');
          resolve(true);
        } else {
          LoggerProvider.info(this, 'Need to go through ATT flow');
          this.segmentProvider.consentIDFA().then((value) => {  // Consent from Firebase 
            if (timeout)
              window.clearTimeout(timeout);
            LoggerProvider.info(this, 'ATT Flow selected response', value);
            // The Android plugin replies "OK" (no payload) on success; only its error path means denied
            const granted = this.platform.is('android') ? value !== false : String(value) === 'true';
            if (granted) {
              this.storageProvider.set(StorageType.ConsentIDFAPermission, 'true');
              this.branch.setDMAParamsForEEA();
              this.trackConsentOptionSelected(true);
            } else {
              this.requestATTPermissionDenied();
              this.trackConsentOptionSelected(false);
            }
            resolve(true);
          })
        }
      } else {
        if (timeout)
          window.clearTimeout(timeout);

        resolve(true); // ATT permission doesn't apply on web
      }
    });
  }

  private trackConsentOptionSelected(optedIn: boolean) {
    if (!this.platform.is('ios')) {
      return;
    }

    this.segmentProvider.trackEvent({
      eventName: AnalyticsEventType.ConsentOptionSelected,
      analyticsData: {
        opt: optedIn ? 'in' : 'out',
        ad_user_data: optedIn,
        ad_personalization: optedIn,
        analytics: optedIn,
        marketing: optedIn
      }
    }).catch(() => LoggerProvider.error(this, LoggerEvent.GeneralException, 'Failed to track ATT consent option selected'));
  }

    async requestATTPermissionDenied() {
      try {
        LoggerProvider.info(this, 'ATT Permission Denied');
        this.branch.setDMAParamsForEEA(false);
        await this.segmentProvider.firebaseAccessDenied();
        await this.storageProvider.set(StorageType.ConsentIDFAPermission, 'false');
      } catch (ex) {
        LoggerProvider.error(this, LoggerEvent.GeneralException, 'ATT Permission Denied Failed');
      }
    }


  createTimeout(resolve, requestName, timeoutMs = 5000) {
    const timeout = window.setTimeout(() => {
      LoggerProvider.error(this, LoggerEvent.ExceptionMessage, `Emergency timeout while waiting for permission (${requestName})`);
      resolve(false);
    }, timeoutMs);
    return timeout;
  }
}



// WEBPACK FOOTER //
// ./src/providers/myria/permission-provider.ts